import React, { useState, useRef, useEffect } from 'react';
import type { MenuLanguage } from './types.js';
import { useAccountController, useAccountSnapshot } from '../../account/context.js';
import { startGoogleSignIn } from '../../account/google-sign-in.js';
import { AccountAvatar } from '../account/AccountAvatar.js';
import '../account/account.css';

const TR: Record<string, Record<MenuLanguage, string>> = {
  guest: { es: 'Invitado', 'en-US': 'Guest', 'en-GB': 'Guest', fr: 'Invité' },
  signIn: {
    es: 'Iniciar sesión con Google',
    'en-US': 'Sign in with Google',
    'en-GB': 'Sign in with Google',
    fr: 'Se connecter avec Google',
  },
  logout: { es: 'Cerrar sesión', 'en-US': 'Log out', 'en-GB': 'Log out', fr: 'Se déconnecter' },
  account: { es: 'Cuenta', 'en-US': 'Account', 'en-GB': 'Account', fr: 'Compte' },
  saved: { es: 'Guardado', 'en-US': 'Saved', 'en-GB': 'Saved', fr: 'Enregistré' },
};

interface AccountBarProps {
  language: MenuLanguage;
}

function t(key: string, lang: MenuLanguage): string {
  return TR[key]?.[lang] || key;
}

export function AccountBar({ language }: AccountBarProps) {
  const account = useAccountController();
  useAccountSnapshot();
  const [open, setOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  const handleGoogleSignIn = async () => {
    await startGoogleSignIn();
  };

  const isRegistered = account.isRegistered;
  const avatarUrl = account.avatarUrl;
  const displayName = account.displayName;

  return (
    <div className="account-trigger-wrapper" ref={dropdownRef}>
      <button className="account-trigger"
        aria-label={t('account', language)}
        title={isRegistered ? displayName : t('guest', language)}
        onClick={() => setOpen((prev) => !prev)}>
        {isRegistered ? (
          <AccountAvatar
            className="account-trigger-avatar"
            sourceUrl={avatarUrl}
            displayName={displayName}
          />
        ) : (
          <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
            <circle cx="12" cy="7" r="4" />
          </svg>
        )}
      </button>

      {open && (
        <div className="account-dropdown">
          {isRegistered ? (
            <>
              <div className="account-dropdown-user">
                <AccountAvatar
                  className="account-dropdown-avatar"
                  sourceUrl={avatarUrl}
                  displayName={displayName}
                />
                <div className="account-dropdown-name">{displayName}</div>
                <div className="account-dropdown-status">{t('saved', language)}</div>
              </div>
              <div className="account-dropdown-actions">
                <button className="account-dropdown-btn logout" onClick={async () => { await account.logout(); setOpen(false); }}>
                  {t('logout', language)}
                </button>
              </div>
            </>
          ) : (
            <div className="account-dropdown-actions">
              <div className="account-dropdown-guest-label">{t('guest', language)}</div>
              <button className="account-dropdown-btn google" onClick={() => void handleGoogleSignIn()}>
                <svg viewBox="0 0 24 24" width="18" height="18">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" />
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                </svg>
                {t('signIn', language)}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
